import React from 'react';
import './Overview.css';

// ── Two-phase model ───────────────────────────────────────────────────────────

const CATALOG_NODES = [
  {
    id: 'product',
    label: 'Product',
    note: 'the service offering',
    children: [
      { id: 'pricing-dimensions', label: 'Pricing Dimensions', note: 'pricing axes' },
      { id: 'meter', label: 'Meters', note: 'how consumption is measured' },
      { id: 'fee',   label: 'Fees',  note: 'what appears on the invoice' },
    ],
  },
  {
    id: 'rate-card',
    label: 'Rate Cards',
    note: 'pricing rule per fee × attribute combination × currency',
    children: [
      { id: 'pricing-models', label: 'Pricing Models', note: 'Fixed, Per Unit, Graduated, Volume, Min/Max' },
    ],
  },
];

const CUSTOMER_NODES = [
  {
    id: 'customer',
    label: 'Customer',
    note: 'legal entity, base currency, tax IDs',
    children: [],
  },
  {
    id: 'contract',
    label: 'Contract',
    note: 'ties customer to fees; billing terms',
    children: [
      { id: 'contract-fee-components', label: 'Fee Settings', note: 'payment mode, discounts, cycle' },
      { id: 'contract-settings',       label: 'Limits & Renewal',  note: 'guardrails, auto-renew, discount' },
    ],
  },
  {
    id: null,
    label: 'Invoice',
    note: 'generated automatically at period end',
    children: [],
    isOutput: true,
  },
];

// ── Reading path ──────────────────────────────────────────────────────────────

const PATH = [
  { id: 'product',          title: 'Products',          why: 'Start here — defines what you sell and its associated pricing dimensions.' },
  { id: 'pricing-dimensions',  title: 'Pricing Dimensions',   why: 'The pricing axes (Zone, Brand, Tier…) that let a fee carry different prices.' },
  { id: 'meter',  title: 'Meters',  why: 'For usage-based products, define the metrics that measure consumption.' },
  { id: 'fee',    title: 'Fees',    why: 'Each component is one billable service and one invoice line item.' },
  { id: 'rate-card',        title: 'Rate Cards',        why: 'Where prices live — scoped by fee, pricing dimension combination, and currency.' },
  { id: 'pricing-models',   title: 'Pricing Models',    why: 'Fixed, Per Unit, Graduated, Volume, Min/Max — pick one per rate card.' },
  { id: 'customer',         title: 'Customers',         why: 'The legal entity — currency, tax IDs, and billing address.' },
  { id: 'contract',         title: 'Contracts',         why: 'Where catalog meets customer. Billing cycles, discounts, and guardrails.' },
];

// ── Page ─────────────────────────────────────────────────────────────────────

function PhaseNode({ node, onNavigate }) {
  const el = node.id
    ? <button className={`ov-node${node.isOutput ? ' ov-node--output' : ''}`} onClick={() => onNavigate(node.id)}>
        <span className="ov-node-label">{node.label}</span>
        <span className="ov-node-note">{node.note}</span>
      </button>
    : <div className="ov-node ov-node--output">
        <span className="ov-node-label">{node.label}</span>
        <span className="ov-node-note">{node.note}</span>
      </div>;

  return (
    <div className="ov-node-wrap">
      {el}
      {node.children && node.children.length > 0 && (
        <div className="ov-node-children">
          {node.children.map((child) => (
            <button key={child.id} className="ov-node-child" onClick={() => onNavigate(child.id)}>
              <span className="ov-node-child-label">{child.label}</span>
              <span className="ov-node-child-note">{child.note}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Overview({ onNavigate }) {
  return (
    <div className="ov-root">

      {/* ── Hero ── */}
      <div className="ov-hero">
        <div className="ov-hero-eyebrow">Documentation</div>
        <h1 className="ov-hero-title">Billing Platform</h1>
        <p className="ov-hero-desc">
          A configurable billing engine for usage-based and subscription pricing.
          Define a product catalog — fees, rate cards, and pricing tiers —
          then attach customers via contracts. Usage events flow in; invoices flow out.
        </p>
      </div>

      {/* ── Two-phase model ── */}
      <div className="ov-section">
        <div className="ov-section-label">How it connects</div>
        <div className="ov-phases">

          <div className="ov-phase">
            <div className="ov-phase-header">
              <span className="ov-phase-tag">Catalog</span>
              <span className="ov-phase-note">Set up once per product</span>
            </div>
            <div className="ov-phase-nodes">
              {CATALOG_NODES.map((node, i) => (
                <React.Fragment key={node.id}>
                  {i > 0 && <div className="ov-phase-arrow">↓</div>}
                  <PhaseNode node={node} onNavigate={onNavigate} />
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="ov-phase">
            <div className="ov-phase-header">
              <span className="ov-phase-tag">Per Customer</span>
              <span className="ov-phase-note">Repeat for each customer</span>
            </div>
            <div className="ov-phase-nodes">
              {CUSTOMER_NODES.map((node, i) => (
                <React.Fragment key={node.label}>
                  {i > 0 && <div className="ov-phase-arrow">↓</div>}
                  <PhaseNode node={node} onNavigate={onNavigate} />
                </React.Fragment>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* ── Reading path ── */}
      <div className="ov-section">
        <div className="ov-section-label">Recommended reading order</div>
        <div className="ov-path">
          {PATH.map((step, i) => (
            <button key={step.id} className="ov-path-card" onClick={() => onNavigate(step.id)}>
              <div className="ov-path-num">{i + 1}</div>
              <div className="ov-path-body">
                <span className="ov-path-title">{step.title}</span>
                <span className="ov-path-why">{step.why}</span>
              </div>
              <span className="ov-path-go">→</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Interactive tools ── */}
      <div className="ov-section">
        <div className="ov-section-label">Interactive tools</div>
        <div className="ov-tools">
          <button className="ov-tool-card" onClick={() => onNavigate('rate-card-calculator')}>
            <span className="ov-tool-icon">⊞</span>
            <div>
              <div className="ov-tool-title">Rate Card Calculator</div>
              <div className="ov-tool-desc">Step through a charge calculation with any pricing model</div>
            </div>
            <span className="ov-tool-arrow">→</span>
          </button>
          <button className="ov-tool-card" onClick={() => onNavigate('contract-create')}>
            <span className="ov-tool-icon">◎</span>
            <div>
              <div className="ov-tool-title">Build a Contract</div>
              <div className="ov-tool-desc">Guided wizard — configure every billing setting step by step</div>
            </div>
            <span className="ov-tool-arrow">→</span>
          </button>
        </div>
      </div>

    </div>
  );
}
